import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { acceptanceTermsApi, fileImportApi } from '@/main/api';
import { disconnectSignalRHub } from '@/main/middlewares/signalRMiddleware';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { logout } from '@/main/services/storage';
import { setModalIsOpened } from '@/main/store/modal';
import { UserTypes, useUserReducer, userSetCurrent } from '@/main/store/user';
import { FileTypeEnum } from '@/main/services/enum/FileTypeEnum';
import { getTranslateByKey } from '@/main/services/utils';
import { isNullUndefined } from '@/main/services/reducerUtils';

interface UseAcceptanceTermsFlowResult {
  isOpen: boolean;
  urlFile: string;
  currentIndex: number;
  isLoadingSave: boolean;
  titleModal: string;
  handleAccept: () => Promise<void>;
  handleDecline: () => Promise<void>;
}

export function useAcceptanceTermsFlow(): UseAcceptanceTermsFlowResult {
  const userReducer = useUserReducer();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [urlFile, setUrlFile] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const pendingAcceptanceTerms = userReducer.currentUser?.pendingAcceptanceTerms ?? [];
  const currentItem = pendingAcceptanceTerms[currentIndex];
  const pendingTermsSignature = pendingAcceptanceTerms
    .map(term => term.fileImportGuid)
    .join('|');
    
  const OBJ_FILE_TYPE = {
    [FileTypeEnum.EULA] : getTranslateByKey('information.eula'),
    [FileTypeEnum.EULAClaims] : getTranslateByKey('information.eulaClaims'),
    [FileTypeEnum.CodeOfEthics] : getTranslateByKey('information.codeOfEthics'),
    [FileTypeEnum.ConflictOfInterest] : getTranslateByKey('information.conflictOfInterest'),
    [FileTypeEnum.PrivacyNotice] : getTranslateByKey('information.privacyNotice'),
    [FileTypeEnum.CodeOfEthicsClaims] : getTranslateByKey('information.codeOfEthicsClaims'),
    [FileTypeEnum.ConflictOfInterestClaims] : getTranslateByKey('information.conflictOfInterestClaims'),
    [FileTypeEnum.PrivacyNoticeClaims] : getTranslateByKey('information.privacyNoticeClaims'),
  } as const;
  
  const keyTitleModal = currentItem ? currentItem.idFileType as keyof typeof OBJ_FILE_TYPE : null;
  const titleModal = !isNullUndefined(keyTitleModal) ? OBJ_FILE_TYPE[keyTitleModal] : '';

  async function loadCurrentFile(guid: string) {
    try {
      setUrlFile('');
      const {
        data: { result }
      } = await fileImportApi.getFileImport(guid);
      setUrlFile(result);
    } catch (error) {}
  }

  async function handleAccept() {
    if (!userReducer.currentUser || !currentItem) return;
    try {
      setIsLoadingSave(true);

      await acceptanceTermsApi.save({ guidFileImport: currentItem.fileImportGuid });

      const isLast = currentIndex >= pendingAcceptanceTerms.length - 1;
      if (isLast) {
        setIsOpen(false);
        dispatch(
          userSetCurrent({
            ...userReducer.currentUser,
            pendingAcceptanceTerms: []
          })
        );
        dispatch(setModalIsOpened(false, null));
      } else {
        setUrlFile('');
        setCurrentIndex(prev => prev + 1);
      }
    } catch (error) {
    } finally {
      setIsLoadingSave(false);
    }
  }

  async function handleDecline() {
    await disconnectSignalRHub();
    logout();
    navigate(RoutesConstants.login);
    dispatch({ type: UserTypes.USER_ISLOGOUT });
  }

  useEffect(() => {
    if (pendingTermsSignature) {
      setCurrentIndex(0);
      setIsOpen(true);
    }
  }, [pendingTermsSignature]);

  useEffect(() => {
    if (isOpen && currentItem?.fileImportGuid) {
      loadCurrentFile(currentItem.fileImportGuid);
    }
  }, [isOpen, currentItem?.fileImportGuid]);

  return {
    isOpen,
    urlFile,
    currentIndex,
    isLoadingSave,
    titleModal,
    handleAccept,
    handleDecline
  };
}
